import { API_ENDPOINTS, SESSION_SCOPED_USER_QUERY_KEY } from "@/constants";
import { isAssistDisplayBlocked } from "@/lib/ui-helper/assist-display";

import {
  createCollection,
  localStorageCollectionOptions,
} from "@tanstack/react-db";

import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { minutesTOms } from "@/lib/utils";
import { fetchWithAuthRedirect } from "@/lib/api/auth-redirect";
import { queryClient } from "@/tanstack-db/query-client";
import { z } from "zod";

import {
  type Assistant,
  assistantSchema,
  type Conversation,
  conversationSchema,
  type ConversationContentRow,
  type ConversationQuestionsItem,
  conversationContentRowSchema,
  conversationQuestionsItemSchema,
  type DocumentType,
  documentTypeSchema,
  type Tag,
  tagSchema,
  userPreferencesSchema,
  viewportSchema,
} from "@/tanstack-db/schemas";

const API_BASE_URL = ""; // Uses same domain as frontend

// ─────────────────────────────────────────────────────────────────────────────
// Document Types Collection (Reference Data - Eager Sync)
// ─────────────────────────────────────────────────────────────────────────────

export const documentTypesCollection = createCollection(
  queryCollectionOptions({
    id: "document-types",
    queryKey: ["ref", "document-types"],
    queryFn: async () => {
      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.DOCUMENT_TYPES}`
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      const data = await res.json();
      // Transform API response to match schema
      return data.map(
        (dt: { tagId: string; label: string; color: string }) => ({
          id: dt.tagId,
          label: dt.label,
          color: dt.color,
        })
      ) as DocumentType[];
    },
    queryClient,
    getKey: (item) => item.id,
    schema: documentTypeSchema,
    staleTime: minutesTOms(60), // 1 hour - reference data rarely changes
  })
);

// ─────────────────────────────────────────────────────────────────────────────
// Assistants Collection (External API data)
// ─────────────────────────────────────────────────────────────────────────────

function isValidAvatarUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  return !url.startsWith("https://dust.tt/static/emojis/");
}

export const assistantsCollection = createCollection(
  queryCollectionOptions({
    id: "assistants",
    queryKey: ["assistants"],
    queryFn: async () => {
      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.ASSISTANTS}`
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      const data = await res.json();
      // Transform API response to match schema
      return data
        .filter((a: { status: string }) => a.status === "active")
        .map(
          (a: {
            sId: string;
            name: string;
            description?: string;
            pictureUrl?: string;
            status: string;
          }) => ({
            id: a.sId,
            label: a.name,
            description: a.description,
            avatarUrl: isValidAvatarUrl(a.pictureUrl)
              ? a.pictureUrl
              : undefined,
            status: a.status,
          })
        ) as Assistant[];
    },
    queryClient,
    getKey: (item) => item.id,
    schema: assistantSchema,
    staleTime: minutesTOms(10), // 10 minutes
  })
);

// ─────────────────────────────────────────────────────────────────────────────
// User Tags Collection (User-specific, with mutations)
// ─────────────────────────────────────────────────────────────────────────────

export const userTagsCollection = createCollection(
  queryCollectionOptions({
    id: "user-tags",
    queryKey: ["user-tags", SESSION_SCOPED_USER_QUERY_KEY],
    queryFn: async () => {
      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.USER_TAGS_ME}`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      const data = await res.json();
      // Transform API response: DbUserTag[] -> Tag[]
      // API returns: { id: string (tagId), label: string, color: string }
      const transformed = data.map(
        (ut: { id: string; label: string; color: string }) => ({
          id: ut.id, // API returns id as the tagId directly
          label: ut.label,
          color: ut.color,
        })
      ) as Tag[];
      return transformed;
    },
    queryClient,
    getKey: (item) => item.id,
    schema: tagSchema,
    staleTime: minutesTOms(5), // 5 minutes

    onInsert: async ({ transaction }) => {
      const newTag = transaction.mutations[0].modified as Tag;
      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.USER_TAGS_ME}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tagId: newTag.id,
            label: newTag.label,
            color: newTag.color,
          }),
        }
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      return { refetch: false };
    },

    onDelete: async ({ transaction }) => {
      const tagId = transaction.mutations[0].key as string;
      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.USER_TAG_ME(tagId)}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      return { refetch: false };
    },

    onUpdate: async ({ transaction }) => {
      const { original, changes } = transaction.mutations[0];
      const tag = { ...original, ...changes } as Tag;
      const nextColor = changes.color ?? tag.color;
      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.USER_TAG_ME(tag.id)}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ color: nextColor }),
        }
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      return { refetch: false };
    },
  })
);

export const conversationQuestionsCollection = createCollection(
  queryCollectionOptions({
    id: "conversation-questions",
    queryKey: ["conversation-questions", SESSION_SCOPED_USER_QUERY_KEY],
    /** Bootstrapped from GET /conversations/db; hydrate may PATCH reconcile. */
    queryFn: async (): Promise<ConversationQuestionsItem[]> => [],
    queryClient,
    getKey: (item) => item.conversationId,
    schema: conversationQuestionsItemSchema,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: minutesTOms(120),
  })
);

function bootstrapConversationQuestionsFromDbList(
  dbConversations: {
    dustConversationId: string;
    questions?: ConversationQuestionsItem["questions"];
  }[]
): void {
  const items: ConversationQuestionsItem[] = dbConversations
    .filter((row) => row.dustConversationId)
    .map((row) => ({
      conversationId: row.dustConversationId,
      questions: row.questions ?? [],
    }));
  if (items.length === 0) return;
  try {
    conversationQuestionsCollection.utils.writeUpsert(items);
  } catch {
    // Collection may not be ready on first conversations fetch — sidebar fills on hydrate.
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Conversations Collection (Main data with CRUD mutations)
// ─────────────────────────────────────────────────────────────────────────────

export const conversationsCollection = createCollection(
  queryCollectionOptions({
    id: "conversations",
    queryKey: ["conversations", SESSION_SCOPED_USER_QUERY_KEY],
    queryFn: async () => {
      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS_DB}`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      const dbConversations = await res.json();

      bootstrapConversationQuestionsFromDbList(dbConversations);

      void import("@/lib/queries/conversation-questions-backfill").then(
        ({ scheduleQuestionsRegistryBackfill }) => {
          scheduleQuestionsRegistryBackfill();
        }
      );

      // Backend now returns conversations with labels embedded (no N+1)
      // Transform to frontend Conversation format
      return dbConversations.map(
        (dbConv: {
          id: number;
          dustConversationId: string;
          title: string | null;
          shareToken?: string | null;
          isPublic?: boolean;
          createdAt: string;
          updatedAt: string;
          startedAt: string;
          lastActivityAt: string;
          documentTypes: string[];
          customTags: string[];
          questions?: ConversationQuestionsItem["questions"];
        }) => ({
          id: dbConv.dustConversationId,
          dbId: dbConv.id,
          title: dbConv.title || "",
          startedAt: dbConv.startedAt,
          lastActivityAt: dbConv.lastActivityAt,
          createdAt: dbConv.createdAt,
          updatedAt: dbConv.updatedAt,
          documentTypes: dbConv.documentTypes || [],
          customTags: dbConv.customTags || [],
          shareToken: dbConv.shareToken ?? null,
          isPublic: dbConv.isPublic ?? false,
        })
      ) as Conversation[];
    },
    queryClient,
    getKey: (item) => item.id,
    schema: conversationSchema,
    staleTime: minutesTOms(5), // 5 minutes - only refresh on new conversation/message events or manual refresh

    onInsert: async ({ transaction }) => {
      const conv = transaction.mutations[0].modified as Conversation;
      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS_DB}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            dustConversationId: conv.id,
            title: conv.title,
            startedAt: conv.startedAt,
            lastActivityAt: conv.lastActivityAt,
          }),
        }
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      const created = await res.json();

      // Add labels if present
      if (
        (conv.documentTypes.length > 0 || conv.customTags.length > 0) &&
        created.id
      ) {
        await Promise.all([
          ...conv.documentTypes.map((tagId) =>
            fetchWithAuthRedirect(
              `${API_BASE_URL}${API_ENDPOINTS.CONVERSATION_LABELS(created.id)}`,
              {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ tagId, labelType: "documentType" }),
              }
            )
          ),
          ...conv.customTags.map((tagId) =>
            fetchWithAuthRedirect(
              `${API_BASE_URL}${API_ENDPOINTS.CONVERSATION_LABELS(created.id)}`,
              {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ tagId, labelType: "userTag" }),
              }
            )
          ),
        ]);
      }

      // Sync server-assigned dbId without a full list refetch (TanStack DB v1).
      conversationsCollection.utils.writeInsert({
        ...conv,
        dbId: created.id as number,
      });
      return { refetch: false };
    },

    onUpdate: async ({ transaction }) => {
      const { original, changes } = transaction.mutations[0];
      const conv = original as Conversation;
      if (!conv.dbId) return { refetch: false };
      if (changes.title === undefined) return { refetch: false };

      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.CONVERSATION(conv.dbId)}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: changes.title,
          }),
        }
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      return { refetch: false };
    },

    onDelete: async ({ transaction }) => {
      const conv = transaction.mutations[0].original as Conversation;
      if (!conv.dbId) return { refetch: false };

      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.CONVERSATION(conv.dbId)}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      return { refetch: false };
    },
  })
);

// ─────────────────────────────────────────────────────────────────────────────
// Conversation content (transcript + CoT) — single source of truth, manual sync
// ─────────────────────────────────────────────────────────────────────────────

export const conversationContentCollection = createCollection(
  queryCollectionOptions({
    id: "conversation-content",
    queryKey: ["conversation-content"],
    queryFn: async (): Promise<ConversationContentRow[]> => [],
    queryClient,
    getKey: (item) => item.conversationId,
    schema: conversationContentRowSchema,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: minutesTOms(120),
  })
);

// ─────────────────────────────────────────────────────────────────────────────
// User Preferences (LocalStorage Collection - cross-tab sync)
// ─────────────────────────────────────────────────────────────────────────────

function getInitialTheme(): "light" | "dark" {
  // Check system preference only (session vierge)
  if (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

function getInitialShowChainOfThought(): boolean {
  try {
    return localStorage.getItem("sidoni_show_chain_of_thought") === "true";
  } catch {
    return false;
  }
}

function getInitialCurrentConversationId(): string | null {
  try {
    return localStorage.getItem("sidoni_current_conversation");
  } catch {
    return null;
  }
}

/**
 * Write a row straight into the storage payload so the collection sees it as
 * already synced on first load.
 *
 * `localStorageCollectionOptions` ignores `initialData`, so the row would
 * otherwise be created by `collection.insert()` on the first write. An insert
 * on a localStorage collection leaves a stale contributor in every live query
 * graph reading it, and the next update on that key throws "Query contributors
 * with the same row key are not congruent". Seeding keeps every mutation on the
 * update path.
 */
function seedLocalStorageRow(
  storageKey: string,
  rowKey: string,
  data: unknown
): void {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(storageKey);
    const payload = raw ? JSON.parse(raw) : {};
    const encodedKey = `s:${rowKey}`;
    if (payload[encodedKey]) return;
    payload[encodedKey] = { versionKey: crypto.randomUUID(), data };
    localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // Storage unavailable or corrupt payload: the collection falls back to
    // creating the row itself.
  }
}

seedLocalStorageRow("sidoni_user_preferences", "user-preferences", {
  key: "user-preferences" as const,
  currentConversationId: getInitialCurrentConversationId(),
  showChainOfThought: getInitialShowChainOfThought(),
  theme: getInitialTheme(),
  sidebarWidth: 250,
  suggestionResponseVisible: {},
  suggestionResponseGateDismissed: false,
  conversationListCollapse: { groups: [], leaves: [] },
});

export const userPreferencesCollection = createCollection(
  localStorageCollectionOptions({
    id: "user-preferences",
    storageKey: "sidoni_user_preferences",
    getKey: (item) => item.key,
    schema: userPreferencesSchema,
  })
);

// ─────────────────────────────────────────────────────────────────────────────
// Viewport Collection (UI State - localStorage, reset on load)
// ─────────────────────────────────────────────────────────────────────────────

seedLocalStorageRow("sidoni_viewport", "viewport", {
  key: "viewport" as const,
  isMobile: typeof window !== "undefined" ? isAssistDisplayBlocked() : false,
});

export const viewportCollection = createCollection(
  localStorageCollectionOptions({
    id: "viewport",
    storageKey: "sidoni_viewport",
    getKey: (item) => item.key,
    schema: viewportSchema,
  })
);

// ─────────────────────────────────────────────────────────────────────────────
// Default User Tags Collection (Reference Data)
// ─────────────────────────────────────────────────────────────────────────────

const defaultUserTagSchema = z.object({
  tagId: z.string(),
  label: z.string(),
  color: z.string(),
});

export const defaultUserTagsCollection = createCollection(
  queryCollectionOptions({
    id: "default-user-tags",
    queryKey: ["ref", "default-user-tags"],
    queryFn: async () => {
      const res = await fetchWithAuthRedirect(
        `${API_BASE_URL}${API_ENDPOINTS.DEFAULT_USER_TAGS}`
      );
      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMessage =
          errorData.error?.message ||
          errorData.error ||
          `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      const data = await res.json();
      return data.map(
        (dt: { tagId: string; label: string; color: string }) => ({
          tagId: dt.tagId,
          label: dt.label,
          color: dt.color,
        })
      );
    },
    queryClient,
    getKey: (item) => item.tagId,
    schema: defaultUserTagSchema,
    staleTime: minutesTOms(60), // 1 hour - reference data rarely changes
  })
);
